<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">3D Models</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage 3D model assets</p>
                                </div>
                                <div class="flex items-center gap-2">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                :disabled="isLoading"
                                                @click="loadModels"
                                        >
                                                <svg class="w-4 h-4" :class="{ 'animate-spin': isLoading }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Refresh
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                @click="openCreateModal"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                </svg>
                                                Create New
                                        </button>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="p-6">
                        <!-- Search Card -->
                        <div class="bg-white rounded-xl shadow-sm py-4 px-6 mb-4">
                                <div class="flex flex-wrap gap-4 items-center">
                                        <div class="flex-1 min-w-[12rem]">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search 3D models..."
                                                                class="w-full py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none"
                                                        >
                                                </div>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <span class="text-xs bg-orange-2 text-orange-7 py-1 px-3 rounded-full font-medium">
                                                        {{ filteredModels.length }} models
                                                </span>
                                        </div>
                                </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading 3D models...</span>
                        </div>

                        <!-- Table Card -->
                        <div v-else class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="overflow-x-auto">
                                        <table class="w-full border-collapse">
                                                <thead>
                                                        <tr class="bg-gray-50">
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-20">ID</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Name</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Scene URL</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Camera</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Auto Rotate</th>
                                                                <th class="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-32">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        <tr v-if="paginatedModels.length === 0">
                                                                <td colspan="6" class="text-center py-12 px-4">
                                                                        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                                        </svg>
                                                                        <p class="text-gray-500 text-sm m-0">No 3D models found</p>
                                                                        <p class="text-gray-400 text-xs mt-1 mb-0">Create a new 3D model to get started</p>
                                                                </td>
                                                        </tr>
                                                        <tr
                                                                v-for="model in paginatedModels"
                                                                :key="model.id"
                                                                class="border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                                                                @click="editModel(model)"
                                                        >
                                                                <td class="py-3 px-4 text-sm text-gray-500 font-mono">{{ model.id }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-900 font-medium">{{ model.name || '—' }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-500">
                                                                        <span v-if="model.sceneUrl" class="block max-w-[12rem] overflow-hidden text-ellipsis whitespace-nowrap" :title="model.sceneUrl">
                                                                                {{ truncateUrl(model.sceneUrl) }}
                                                                        </span>
                                                                        <span v-else class="text-gray-400">—</span>
                                                                </td>
                                                                <td class="py-3 px-4 text-xs text-gray-500">
                                                                        α:{{ model.cameraAlpha || 0 }} β:{{ model.cameraBeta || 0 }} r:{{ model.cameraRadius || 0 }}
                                                                </td>
                                                                <td class="py-3 px-4">
                                                                        <span
                                                                                :class="[
                                                                                        'inline-flex items-center py-1 px-2 text-xs font-medium rounded-full',
                                                                                        model.cameraAutoRotate === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
                                                                                ]"
                                                                        >
                                                                                {{ model.cameraAutoRotate === 1 ? 'Yes' : 'No' }}
                                                                        </span>
                                                                </td>
                                                                <td class="py-3 px-4 text-right">
                                                                        <div class="flex justify-end gap-2">
                                                                                <button
                                                                                        type="button"
                                                                                        class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7"
                                                                                        title="Edit"
                                                                                        @click.stop="editModel(model)"
                                                                                >
                                                                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                        </svg>
                                                                                </button>
                                                                                <button
                                                                                        type="button"
                                                                                        class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-red-600"
                                                                                        title="Delete"
                                                                                        @click.stop="confirmDeleteModel(model)"
                                                                                >
                                                                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                        </svg>
                                                                                </button>
                                                                        </div>
                                                                </td>
                                                        </tr>
                                                </tbody>
                                        </table>
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="flex justify-between items-center py-4 px-6 border-t border-gray-200">
                                        <div class="text-sm text-gray-500">
                                                Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredModels.length) }} of {{ filteredModels.length }} models
                                        </div>
                                        <div class="flex gap-2">
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage--"
                                                >
                                                        Previous
                                                </button>
                                                <button
                                                        v-for="page in visiblePages"
                                                        :key="page"
                                                        type="button"
                                                        class="py-2 px-3 border rounded-md text-sm cursor-pointer min-w-[2.5rem]"
                                                        :class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300'"
                                                        @click="currentPage = page"
                                                >
                                                        {{ page }}
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage++"
                                                >
                                                        Next
                                                </button>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Create Modal -->
                <div v-if="showCreateModal" class="fixed inset-0 z-50 flex items-center justify-center">
                        <div class="absolute inset-0 bg-black/50" @click="closeCreateModal"></div>
                        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-md m-4">
                                <div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">Create New 3D Model</h3>
                                        <button
                                                type="button"
                                                class="text-gray-400 bg-transparent border-none cursor-pointer p-1"
                                                @click="closeCreateModal"
                                        >
                                                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6">
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                                <input
                                                        v-model="newModelName"
                                                        type="text"
                                                        placeholder="Enter 3D model name"
                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border"
                                                        @keydown.enter="createModel"
                                                >
                                        </div>
                                </div>
                                <div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                                        <button
                                                type="button"
                                                class="py-2.5 px-4 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm cursor-pointer"
                                                @click="closeCreateModal"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                type="button"
                                                :disabled="!newModelName.trim() || isCreating"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                @click="createModel"
                                        >
                                                <svg v-if="isCreating" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                </svg>
                                                {{ isCreating ? 'Creating...' : 'Create' }}
                                        </button>
                                </div>
                        </div>
                </div>

                <!-- Toast Notification -->
                <div v-if="toastMessage" class="fixed bottom-6 right-6 z-50">
                        <div
                                class="flex items-center gap-3 py-4 px-5 rounded-lg shadow-lg"
                                :class="toastVariant === 'success' ? 'bg-emerald-600' : 'bg-red-600'"
                        >
                                <svg v-if="toastVariant === 'success'" class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <svg v-else class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span class="text-sm font-medium text-white">{{ toastMessage }}</span>
                                <button
                                        type="button"
                                        class="ml-2 text-white opacity-70 bg-transparent border-none cursor-pointer p-0"
                                        @click="hideToast"
                                >
                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                </button>
                        </div>
                </div>
        </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import Model3DService from 'services/Model3DService';
import { confirmDelete } from 'utils/swal';

export default defineComponent({
        name: 'Model3DsPage',
        setup() {
                const router = useRouter();
                const auth0 = useAuth0();

                const models = ref([]);
                const isLoading = ref(false);
                const searchQuery = ref('');
                const currentPage = ref(1);
                const pageSize = 20;
                const showCreateModal = ref(false);
                const newModelName = ref('');
                const isCreating = ref(false);

                const toastMessage = ref('');
                const toastVariant = ref('success');
                let toastTimeout;

                const loggedIn = computed(() => auth0.isAuthenticated.value);

                const filteredModels = computed(() => {
                        if (!searchQuery.value.trim()) {
                                return models.value;
                        }
                        const search = searchQuery.value.trim().toLowerCase();
                        return models.value.filter(m =>
                                (m.name && m.name.toLowerCase().includes(search)) ||
                                (m.id && m.id.toString().includes(search)) ||
                                (m.sceneUrl && m.sceneUrl.toLowerCase().includes(search))
                        );
                });

                const totalPages = computed(() => Math.ceil(filteredModels.value.length / pageSize) || 1);

                const paginatedModels = computed(() => {
                        const start = (currentPage.value - 1) * pageSize;
                        return filteredModels.value.slice(start, start + pageSize);
                });

                const visiblePages = computed(() => {
                        const pages = [];
                        const maxVisible = 5;
                        let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
                        let end = Math.min(totalPages.value, start + maxVisible - 1);
                        if (end - start + 1 < maxVisible) {
                                start = Math.max(1, end - maxVisible + 1);
                        }
                        for (let i = start; i <= end; i++) {
                                pages.push(i);
                        }
                        return pages;
                });

                const showToast = (message, variant = 'success') => {
                        toastMessage.value = message;
                        toastVariant.value = variant;
                        if (toastTimeout) clearTimeout(toastTimeout);
                        toastTimeout = setTimeout(() => {
                                toastMessage.value = '';
                        }, 4000);
                };

                const hideToast = () => {
                        toastMessage.value = '';
                        if (toastTimeout) clearTimeout(toastTimeout);
                };

                const truncateUrl = (url) => {
                        if (!url) return '';
                        const filename = url.split('/').pop();
                        return filename.length > 30 ? filename.substring(0, 30) + '...' : filename;
                };

                const loadModels = async () => {
                        if (!loggedIn.value) return;

                        isLoading.value = true;
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                const response = await Model3DService.getAll(params);
                                models.value = Array.isArray(response) ? response : [];
                        } catch (error) {
                                showToast(error.message || 'Failed to load 3D models', 'error');
                        } finally {
                                isLoading.value = false;
                        }
                };

                const openCreateModal = () => {
                        newModelName.value = '';
                        showCreateModal.value = true;
                };

                const closeCreateModal = () => {
                        showCreateModal.value = false;
                        newModelName.value = '';
                };

                const createModel = async () => {
                        if (!newModelName.value.trim() || isCreating.value) return;
                        isCreating.value = true;
                        try {
                                const newModel = await Model3DService.create({ name: newModelName.value.trim() });
                                closeCreateModal();
                                showToast('3D Model created successfully');
                                router.push({ name: 'EditModel3D', params: { id: newModel.id } });
                        } catch (error) {
                                showToast(error.message || 'Failed to create 3D model', 'error');
                        } finally {
                                isCreating.value = false;
                        }
                };

                const editModel = (model) => {
                        router.push({ name: 'EditModel3D', params: { id: model.id } });
                };

                const confirmDeleteModel = async (model) => {
                        const result = await confirmDelete();
                        if (result.isConfirmed) {
                                try {
                                        await Model3DService.delete(model.id);
                                        await loadModels();
                                        showToast('3D Model deleted successfully');
                                } catch (error) {
                                        showToast(error.message || 'Failed to delete 3D model', 'error');
                                }
                        }
                };

                watch(loggedIn, (isLoggedIn) => {
                        if (isLoggedIn) {
                                loadModels();
                        }
                }, { immediate: true });

                watch(searchQuery, () => {
                        currentPage.value = 1;
                });

                return {
                        models,
                        isLoading,
                        searchQuery,
                        currentPage,
                        pageSize,
                        totalPages,
                        filteredModels,
                        paginatedModels,
                        visiblePages,
                        showCreateModal,
                        newModelName,
                        isCreating,
                        toastMessage,
                        toastVariant,
                        truncateUrl,
                        openCreateModal,
                        closeCreateModal,
                        createModel,
                        editModel,
                        confirmDeleteModel,
                        hideToast,
                };
        },
});
</script>
